import { useEffect, useRef, useState, type PointerEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { adminApi } from '@/api/endpoints'
import { streamFrames } from '@/api/liveStream'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import type { LiveStatus } from '@/types/api'

type Point = { x: number; y: number }
type Mode = 'tap' | 'hold'

const HOLD_SECONDS = [3, 5, 8, 12] as const
/** A pointer that moved less than this (CSS px) between down and up is a tap, not a drag. */
const DRAG_THRESHOLD = 10

/**
 * A user's parked page, live, for the administrator to clear the check on.
 *
 * The picture is the page in the worker's browser. What is done on it is
 * relayed there as it was made: a tap is a click, a press-and-move is a drag
 * (sliders, puzzle pieces), and in Hold mode a tap presses for the seconds
 * chosen ("press and hold" checks). Short text can be typed into whatever was
 * clicked last. Nothing here solves anything by itself.
 *
 * The picture scales to the container and keeps the real viewport's aspect
 * ratio; a point is mapped back through the rendered size, so it lands where
 * the finger did however small the screen.
 */
export function LivePanel({ handoffId, status }: { handoffId: string; status: LiveStatus }) {
  const queryClient = useQueryClient()
  const shotKey = ['live', handoffId, 'shot'] as const

  // The picture. Pushed by the server as the page changes; if that stream
  // cannot be had (a proxy that buffers, a dropped line) it is asked for instead.
  const [frame, setFrame] = useState<Blob | null>(null)
  const [pushed, setPushed] = useState<boolean | null>(null)
  useEffect(() => {
    const abort = new AbortController()
    let got = false
    streamFrames(
      handoffId,
      (jpeg) => {
        if (!got) setPushed(true)
        got = true
        setFrame(jpeg)
      },
      abort.signal,
    ).catch(() => {
      if (!abort.signal.aborted) setPushed(false)
    })
    return () => abort.abort()
  }, [handoffId])
  const shot = useQuery({
    queryKey: shotKey,
    queryFn: () => adminApi.liveScreenshot(handoffId),
    enabled: pushed === false,
    refetchInterval: 1200,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })
  const picture = pushed === false ? (shot.data ?? frame) : frame
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!picture) return
    const objectUrl = URL.createObjectURL(picture)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [picture])

  // Without the stream: the worker refreshes its picture shortly after an input, so ask for it then.
  const refreshSoon = () => {
    if (pushed === false) window.setTimeout(() => void queryClient.invalidateQueries({ queryKey: shotKey }), 450)
  }
  // The run may have just moved on; the status poll will say so.
  const quiet = { onSuccess: refreshSoon, onError: () => undefined }

  const click = useMutation({ mutationFn: (at: Point) => adminApi.liveClick(handoffId, at.x, at.y), ...quiet })
  const hold = useMutation({ mutationFn: ({ at, ms }: { at: Point; ms: number }) => adminApi.liveHold(handoffId, at.x, at.y, ms), ...quiet })
  const drag = useMutation({ mutationFn: ({ from, to, ms }: { from: Point; to: Point; ms: number }) => adminApi.liveDrag(handoffId, from, to, ms), ...quiet })
  const [typed, setTyped] = useState('')
  const send = useMutation({ mutationFn: () => adminApi.liveType(handoffId, typed), onSuccess: () => { setTyped(''); refreshSoon() } })

  const [mode, setMode] = useState<Mode>('tap')
  // Fit shows the whole page; actual size shows it pixel for pixel in a box that scrolls, for small print and picture puzzles.
  const [zoomed, setZoomed] = useState(false)
  const [holdSeconds, setHoldSeconds] = useState<number>(HOLD_SECONDS[0])

  const width = status.width ?? 1366
  const height = status.height ?? Math.round((width * 9) / 16)

  // The gesture in progress, in the picture's own (rendered) pixels.
  const start = useRef<(Point & { at: number }) | null>(null)
  const [trail, setTrail] = useState<{ from: Point; to: Point } | null>(null)
  const [pulse, setPulse] = useState<(Point & { key: number }) | null>(null)

  const local = (event: PointerEvent<HTMLElement>): Point => {
    const rect = event.currentTarget.getBoundingClientRect()
    return { x: Math.min(Math.max(0, event.clientX - rect.left), rect.width), y: Math.min(Math.max(0, event.clientY - rect.top), rect.height) }
  }
  const toPage = (event: PointerEvent<HTMLElement>, point: Point): Point => {
    const rect = event.currentTarget.getBoundingClientRect()
    return { x: point.x * (width / rect.width), y: point.y * (height / rect.height) }
  }

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    const point = local(event)
    start.current = { ...point, at: performance.now() }
    setTrail({ from: point, to: point })
  }
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!start.current) return
    setTrail({ from: start.current, to: local(event) })
  }
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const began = start.current
    start.current = null
    setTrail(null)
    if (!began) return
    const end = local(event)
    const moved = Math.hypot(end.x - began.x, end.y - began.y) > DRAG_THRESHOLD
    setPulse({ ...end, key: Date.now() })
    if (moved) {
      const ms = Math.round(Math.min(Math.max(performance.now() - began.at, 300), 10_000))
      drag.mutate({ from: toPage(event, began), to: toPage(event, end), ms })
    } else if (mode === 'hold') {
      hold.mutate({ at: toPage(event, began), ms: holdSeconds * 1000 })
    } else {
      click.mutate(toPage(event, began))
    }
  }
  const onPointerCancel = () => {
    start.current = null
    setTrail(null)
  }

  const busy = hold.isPending || drag.isPending

  return (
    <div className="space-y-3">
      <div className={cn(zoomed && 'overflow-auto border-y border-line bg-surface-muted sm:rounded-field sm:border')} style={zoomed ? { maxHeight: 'max(320px, calc(100dvh - 410px))' } : undefined}>
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        // The picture is the work surface: a finger on it draws on the page, it does not scroll this one.
        className={cn(
          'relative select-none overflow-hidden bg-surface',
          // Fitted, the picture is the work surface and a finger on it never scrolls this page. At actual size a finger pans the box; taps still land.
          zoomed ? 'touch-pan-x touch-pan-y' : 'mx-auto touch-none border-y border-line sm:rounded-field sm:border',
          mode === 'hold' ? 'cursor-cell' : 'cursor-crosshair',
        )}
        // Fitted: as wide as the column, but never so tall that the controls under it leave the screen.
        style={{ aspectRatio: `${width} / ${height}`, width: zoomed ? `${width}px` : `min(100%, max(300px, calc((100dvh - 410px) * ${(width / height).toFixed(4)})))` }}
        role="img"
        aria-label="The user's page, live. Tap to click, drag to slide."
      >
        {url ? (
          <img src={url} alt="" draggable={false} className={cn('pointer-events-none block h-full w-full', busy && 'opacity-80')} />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[13px] text-ink-muted">
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />
              Loading the live page…
            </span>
          </div>
        )}

        {/* What the pointer is doing, drawn over the picture. */}
        {trail && Math.hypot(trail.to.x - trail.from.x, trail.to.y - trail.from.y) > DRAG_THRESHOLD && (
          <svg className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
            <line x1={trail.from.x} y1={trail.from.y} x2={trail.to.x} y2={trail.to.y} stroke="rgb(var(--info))" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="1 6" />
            <circle cx={trail.from.x} cy={trail.from.y} r="5" fill="rgb(var(--info))" />
            <circle cx={trail.to.x} cy={trail.to.y} r="7" fill="none" stroke="rgb(var(--info))" strokeWidth="2.5" />
          </svg>
        )}
        {pulse && (
          <span
            key={pulse.key}
            onAnimationEnd={() => setPulse(null)}
            className="pointer-events-none absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full bg-info/60 [animation-iteration-count:1]"
            style={{ left: pulse.x, top: pulse.y }}
            aria-hidden="true"
          />
        )}
        {hold.isPending && (
          <span className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded-pill bg-ink px-3 py-1.5 text-[12.5px] font-medium text-oncolor shadow-float">
            Holding for {holdSeconds}s…
          </span>
        )}
        {drag.isPending && (
          <span className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded-pill bg-ink px-3 py-1.5 text-[12.5px] font-medium text-oncolor shadow-float">
            Dragging…
          </span>
        )}
      </div>
      </div>

      <div className="flex flex-col gap-2.5 px-4 sm:px-0">
        <div className="flex flex-wrap items-center gap-2">
          <div role="radiogroup" aria-label="What a tap does" className="inline-flex rounded-field bg-surface-muted p-1">
            {(['tap', 'hold'] as const).map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={mode === value}
                onClick={() => setMode(value)}
                className={cn('h-8 rounded-[8px] px-3 text-[13px] font-medium transition-colors', mode === value ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink')}
              >
                {value === 'tap' ? 'Tap' : 'Press and hold'}
              </button>
            ))}
          </div>
          {mode === 'hold' && (
            <div role="radiogroup" aria-label="Hold for" className="inline-flex rounded-field bg-surface-muted p-1">
              {HOLD_SECONDS.map((seconds) => (
                <button
                  key={seconds}
                  type="button"
                  role="radio"
                  aria-checked={holdSeconds === seconds}
                  onClick={() => setHoldSeconds(seconds)}
                  className={cn('num h-8 rounded-[8px] px-2.5 text-[13px] font-medium transition-colors', holdSeconds === seconds ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink')}
                >
                  {seconds}s
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            aria-pressed={zoomed}
            onClick={() => setZoomed((value) => !value)}
            title={zoomed ? 'Show the whole page' : 'Show the page at its real size'}
            className={cn('h-10 rounded-field border px-3 text-[13px] font-medium transition-colors', zoomed ? 'border-ink bg-ink text-oncolor' : 'border-line bg-surface text-ink-soft hover:bg-surface-muted')}
          >
            {zoomed ? 'Fit page' : 'Actual size'}
          </button>
          <p className="min-w-0 flex-1 basis-full text-[12.5px] text-ink-muted sm:basis-0">
            {mode === 'hold' ? `Tap the button on the page: it is pressed for ${holdSeconds} seconds.` : 'Tap to click. Press and move to drag a slider or a puzzle piece.'}
          </p>
        </div>

        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            if (typed.trim()) send.mutate()
          }}
        >
          <input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            maxLength={200}
            autoComplete="off"
            aria-label="Text to type into the page"
            placeholder="Letters to type? Tap the box on the page first, then send."
            className="field-box h-11 min-w-0 flex-1 py-0 text-[16px]"
          />
          <Button type="submit" disabled={!typed.trim()} loading={send.isPending} className="shrink-0">
            Type it
          </Button>
        </form>
        {send.isError && <p className="text-[12.5px] text-danger">That could not be typed. The page may have moved on.</p>}
      </div>
    </div>
  )
}
